import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, MessageSquare, ArrowLeft } from "lucide-react";

const Messages = () => {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const activeId = params.get("c");
  const [convs, setConvs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const [mobileThread, setMobileThread] = useState(false);
  const bottomRef = useRef(null);

  const loadConvs = () => api.get("/conversations").then((r) => setConvs(r.data)).catch(() => {});
  const loadMessages = (id) => api.get(`/conversations/${id}/messages`).then((r) => { setMessages(r.data.messages); setOther(r.data.other_user); }).catch(() => {});

  useEffect(() => { loadConvs(); const t = setInterval(loadConvs, 6000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (!activeId) return;
    loadMessages(activeId); setMobileThread(true);
    const t = setInterval(() => loadMessages(activeId), 4000);
    return () => clearInterval(t);
  }, [activeId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const openConv = (id) => setParams({ c: id });

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeId) return;
    const t = text; setText("");
    try { await api.post(`/conversations/${activeId}/messages`, { text: t }); loadMessages(activeId); loadConvs(); }
    catch (err) { setText(t); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="font-display text-3xl font-semibold mb-6">Messages</h1>
      <Card className="glass overflow-hidden">
        <div className="grid md:grid-cols-3 min-h-[60vh]">
          {/* Inbox */}
          <div className={`md:col-span-1 border-r border-white/10 ${mobileThread ? "hidden md:block" : "block"}`} data-testid="messages-inbox-list">
            <div className="p-3 border-b border-white/10 text-sm text-muted-cosmic">Conversations</div>
            <ScrollArea className="h-[52vh]">
              {convs.length === 0 ? (
                <div className="p-8 text-center text-muted-cosmic text-sm"><MessageSquare className="mx-auto mb-2 text-[#7C3AED]" /> No conversations yet.</div>
              ) : convs.map((c) => (
                <button key={c.conversation_id} onClick={() => openConv(c.conversation_id)} className={`w-full text-left p-3 flex items-center gap-3 hover:bg-white/6 transition-colors ${activeId === c.conversation_id ? "bg-white/8" : ""}`}>
                  <Avatar className="h-10 w-10"><AvatarImage src={c.other_user?.picture} /><AvatarFallback className="bg-[#3B82F6] text-white text-xs">{(c.other_user?.name||"?").slice(0,2)}</AvatarFallback></Avatar>
                  <div className="min-w-0 flex-1"><div className="text-white text-sm truncate">{c.other_user?.name}</div><div className="text-xs text-muted-cosmic truncate">{c.last_message || "Say hi 👋"}</div></div>
                </button>
              ))}
            </ScrollArea>
          </div>

          {/* Thread */}
          <div className={`md:col-span-2 flex flex-col ${mobileThread ? "block" : "hidden md:flex"}`} data-testid="messages-thread">
            {activeId && other ? (
              <>
                <div className="p-3 border-b border-white/10 flex items-center gap-3">
                  <button className="md:hidden text-white" onClick={() => setMobileThread(false)}><ArrowLeft size={18} /></button>
                  <Avatar className="h-9 w-9"><AvatarImage src={other.picture} /><AvatarFallback className="bg-[#3B82F6] text-white text-xs">{(other.name||"?").slice(0,2)}</AvatarFallback></Avatar>
                  <div className="text-white">{other.name}</div>
                </div>
                <ScrollArea className="flex-1 p-4 h-[40vh]">
                  <div className="space-y-3">
                    {messages.map((m) => {
                      const mine = m.sender_id === user?.user_id;
                      return (
                        <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? "bg-[rgba(124,58,237,0.28)] border border-[rgba(124,58,237,0.3)]" : "bg-white/6 border border-white/10"}`}>
                            <p className="text-sm text-white">{m.text}</p>
                            <p className="font-mono-cosmic text-[10px] text-white/45 mt-1">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={bottomRef} />
                  </div>
                </ScrollArea>
                <form onSubmit={send} className="p-3 border-t border-white/10 flex items-end gap-2">
                  <Textarea data-testid="messages-composer-textarea" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }} placeholder="Type a message…" className="bg-white/5 border-white/10 text-white min-h-11 max-h-28 resize-none" />
                  <Button data-testid="messages-send-button" type="submit" className="h-11 bg-[hsl(var(--primary))] text-white glow-violet hover:glow-cyan"><Send size={16} /></Button>
                </form>
              </>
            ) : (
              <div className="flex-1 grid place-items-center text-muted-cosmic"><div className="text-center"><MessageSquare className="mx-auto mb-2 text-[#7C3AED]" size={32} /> Select a conversation</div></div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Messages;
